// Whether the times the office is about to hold would leave anything for
// anyone else — and how to say so.
//
// From the acceptance pass: three 90-minute consultations plus the buffer
// covered every slot on a Wednesday, so the second patient who messaged could
// be offered nothing. That is the holds doing exactly what the practice asked
// for, and exactly what John warned about in the same breath — and the app
// gave the office no signal at all. This is the signal. A warning, never a
// gate: the office may well mean it.
//
// The numbers come from the server's own availability engine (buffer, working
// hours, existing holds included). This only decides whether they are worth a
// sentence, and writes it.
import { patientDayLabel } from '../../pages/publicBookingTime';

export interface HoldImpactRow {
  date: string;
  held: number;
  freeBefore: number;
  freeAfter: number;
  /** Whose day it is — set when the holds are in more than one diary. */
  who?: string | null;
}

export type HoldImpactLevel = 'none' | 'most' | 'all';

export interface HoldImpact {
  level: HoldImpactLevel;
  message: string | null;
}

const these = (n: number) => (n === 1 ? 'This held time' : `These ${n} held times`);

export function describeHoldImpact(rows: HoldImpactRow[] | null | undefined): HoldImpact {
  const all: string[] = [];
  const most: string[] = [];
  for (const r of rows || []) {
    // A day that was already full is not these holds' doing.
    if (!r || r.freeBefore <= 0) continue;
    // A lab draw with the nurse and a visit with the doctor are held in two
    // diaries; "all of Monday" alone would not say whose Monday.
    const day = `${patientDayLabel(r.date)}${r.who ? ` with ${r.who}` : ''}`;
    if (r.freeAfter <= 0) {
      all.push(`${these(r.held)} would take all of ${day} — nobody else could be offered that day.`);
    } else if (r.freeAfter === 1) {
      most.push(`${these(r.held)} would leave only one other time on ${day}.`);
    }
  }
  if (!all.length && !most.length) return { level: 'none', message: null };
  const level: HoldImpactLevel = all.length ? 'all' : 'most';
  const advice = all.length ? ' Hold fewer, or spread them across days.' : '';
  return { level, message: [...all, ...most].join(' ') + advice };
}
